<template>
  <div id="regBylink-container">
    <!-- Show loading progress indicator when the component is loading -->
    <v-overlay v-if="loading" :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <v-row v-else class="px-2 text-styling">
      <v-col
        lg="6"
        md="6"
        sm="12"
        xs="12"
        offset-lg="3"
        offset-md="3"
        offset-sm="0"
        justify="center"
        class="pa-0"
      >
        <!-- Show the registration form if there is no error and it has not been processed already -->
        <div v-if="!error && !checkStatus && !alreadyProcessed" class="mt-4">
          <v-col v-if="checkout.theme.logo" align="center">
            <v-img
              :src="require(`@/assets/${checkout.theme.logo}`)"
              @load="onImageLoad"
              :style="merchantLogoImageStyle"
              ref="merchantLogo"
            ></v-img>
          </v-col>

          <v-col class="title" align="center">
            <h2>{{ title }}</h2>
            <p>{{ subtitle }}</p>
          </v-col>

          <v-col md="12">
            <v-card>
              <v-divider></v-divider>

              <div class="px-4 py-2 pb-2">
                <!-- Show test transaction alert when it is a test transaction -->
                <v-row v-if="test" class="mt-1 mb-1">
                  <v-col cols="12" class="d-flex align-center justify-center">
                    <v-alert dense type="info">
                      This is a test transaction
                    </v-alert>
                  </v-col>
                </v-row>

                <!-- Display the reference and product information -->
                <v-row class="mt-4">
                  <v-col md="12" class="py-0">
                    <v-text-field
                      v-model="checkout.regbylink.reference"
                      label="Reference"
                      outlined
                      disabled
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col md="12" class="py-0">
                    <v-text-field
                      v-model="checkout.regbylink.product"
                      label="Product"
                      outlined
                      disabled
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Display the initial payment amount -->
                <v-row v-if="checkout.regbylink.amount">
                  <v-col md="12" class="py-0">
                    <v-text-field
                      v-model="checkout.regbylink.amountCurrency"
                      label="Initial Payment Amount"
                      outlined
                      disabled
                      readonly
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Display the schedule information if it exists -->
                <div v-if="showScheduleInfo" class="mt-0">
                  <v-row class="mt-2">
                    <v-col>
                      <span class="text-field-header"
                        >Schedule Information
                      </span>
                    </v-col>
                  </v-row>

                  <!-- Display the schedule information -->
                  <v-row class="mt-6">
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.amountCurrency"
                        label="Amount"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.numberOfPayments"
                        label="Number of Payments"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.frequency"
                        label="Frequency"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.collectionDay"
                        label="Collection Day"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.startDate"
                        label="Start Date"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.endDate"
                        label="End Date"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col md="12" class="py-0">
                      <v-text-field
                        v-model="checkout.schedule.firstExecutionDate"
                        label="First Payment Date"
                        outlined
                        disabled
                        readonly
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>

                <!-- Display the copy-and-pay form -->
                <copy-and-pay
                  class="mt-1"
                  :formAction="formAction"
                  :gatewayId="checkout.gatewayId"
                  :checkoutId="checkout.checkoutId"
                  :billingData="checkout.billing"
                  submitButtonText="Register Card"
                ></copy-and-pay>
              </div>
            </v-card>

            <!-- Merchant specific footers -->
            <v-row v-if="footer" class="text-caption px-5 mt-5 footer-text">
              {{ footer }}
            </v-row>
          </v-col>
        </div>

        <!-- RBL already processed -->
        <div v-if="alreadyProcessed" class="mt-4" align="center">
          <v-img
            :src="require(`@/assets/${checkout.theme.logo}`)"
            width="100"
          ></v-img>
          <v-col class="title" align="center">
            <h2>{{ title }}</h2>
          </v-col>
          <p>
            Reg-by-link has either, already been paid, or is no longer active
          </p>
        </div>

        <!-- Display payment status -->
        <div v-if="checkStatus && !loading">
          <v-overlay>
            <v-col align="center">
              <v-card style="background-color: white; color: black;">
                <v-card-text style="color: black;">
                  <!-- Show success or failure message based on payment status -->
                  <div v-if="isPaymentSuccessful">
                    <v-icon x-large color="green">mdi-check-circle</v-icon>
                    <h4>Transaction Successful</h4>
                  </div>
                  <div v-else>
                    <v-icon x-large color="red">mdi-cancel</v-icon>
                    <h4>Transaction Unsuccessful</h4>
                  </div>
                  <p>{{ paymentStatus.description }}</p>
                </v-card-text>
                <!-- Show retry button if payment was unsuccessful -->
                <v-card-actions v-if="!isPaymentSuccessful">
                  <v-spacer></v-spacer>
                  <v-btn class="primary" @click="retry">Retry</v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-overlay>
        </div>

        <!-- Error -->
        <div v-else class="mt-4" align="center">
          {{ errorMessage }}
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";
import { formatCurrencyAmount } from "@/utils/currencyAmount.js";
import { findFooterByMerchantUuid } from "@/components/regByLink/footerConstants.js";

import CopyAnyPay from "@/components/copyandpay/CopyAndPay.vue";

export default {
  components: {
    "copy-and-pay": CopyAnyPay,
  },

  data() {
    return {
      title: "Reg By Link",
      subtitle: "Provide your card details to register a new payment method.",
      hash: this.$route.params.id,
      loading: false,
      test: null,
      checkStatus: false,
      error: false,
      errorMessage: null,
      merchantLogoImageStyle: {
        width: "100",
      },

      checkout: {
        gatewayId: null,
        checkoutId: null,
        regbylink: {
          reference: null,
          product: null,
          amount: null,
          amountCurrency: null,
          currency: null,
          merchantUuid: null,
          processed: null,
        },
        schedule: {
          amount: null,
          amountCurrency: null,
          currency: null,
          numberOfPayments: null,
          frequency: null,
          collectionDay: null,
          startDate: null,
          endDate: null,
          executionPlan: null,
          firstExecutionDate: null,
        },
        billing: {
          street1: "",
          street2: "",
          city: "",
          state: "",
          postcode: "",
          country: "",
        },
        theme: {
          merchantName: null,
          logo: null,
        },
      },

      paymentStatus: {
        id: null,
        code: null,
        description: null,
        success: false,
      },
    };
  },

  computed: {
    showScheduleInfo() {
      return (
        this.checkout.schedule.amount != null &&
        this.checkout.schedule.executionPlan != null
      );
    },

    footer() {
      return findFooterByMerchantUuid(this.checkout.regbylink.merchantUuid);
    },

    alreadyProcessed() {
      const processed = this.checkout.regbylink.processed ?? null;
      return processed != null && processed == 1;
    },

    isPaymentSuccessful() {
      return this.paymentStatus.success;
    },

    // Split the path at the first occurrence of "/check-status"
    // If "/check-status" doesn't exist, the whole path becomes the base
    // This also accounts for multiple occurrences of "/check-status"
    basePath() {
      return this.$route.path.split('/check-status')[0]
    },

    // Form action should be completely clean of any query params,
    // pointing to the current component's "/check-status" child path
    formAction() {
      const { origin } = window.location;
      return `${origin}${this.basePath}/check-status`
    }
  },

  created() {
    // Set loading state to true and check status to false
    this.loading = true;
    this.checkStatus = false;

    if (this.$route.path.includes("/check-status")) {
      // Check payment status after CNP form action redirect to RBL child component path
      this.checkPaymentStatus();
    } else {
      this.getRegByLink();
    }
  },

  methods: {
    // This function sends a POST request to "reg-by-link/get-data" API endpoint with "hash" data.
    // The response will contain the data necessary to create the payment checkout.
    getRegByLink() {
      axios({
        method: "post",
        url: "reg-by-link/get-data",
        data: {
          hash: this.hash,
        },
        config: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
        .then((response) => {
          // extract the checkout data from the response
          const checkoutData = response.data;

          // set the value of the "test" property based on the gateway ID
          if (checkoutData.gatewayId == 1) {
            this.test = false;
          } else if (checkoutData.gatewayId == 2) {
            this.test = true;
          }

          // if the reg-by-link has already been processed, handle it
          if (checkoutData.processed == 1) {
            this.handleAlreadyProcessed();
            return;
          }

          // format the amount currency for the reg-by-link and schedule
          checkoutData.regbylink.amountCurrency = formatCurrencyAmount(
            checkoutData.regbylink.amount,
            checkoutData.regbylink.currency
          );

          checkoutData.schedule.amountCurrency = formatCurrencyAmount(
            checkoutData.schedule.amount,
            checkoutData.schedule.currency
          );

          // set the value of the "checkout" property to the checkout data
          this.checkout = checkoutData;

          // set the loading and error states to false
          this.loading = false;
          this.error = false;
        })
        .catch((e) => {
          // set the loading and error states to false and true, respectively
          this.loading = false;
          this.error = true;

          // log the error
          console.error(e);

          // handle the request error
          this.handleRequestError(e);
        });
    },

    checkPaymentStatus() {
      this.checkStatus = true;

      // get latest rPath from the query params before we clean the current router path
      const rPath = this.getLatestResourcePath();

      // Replace with the base path, once we've got the latest rPath
      // we don't need to preserve the query params as we will need to do a new
      // CNP checkout anways.
      this.$router.replace({ path: this.basePath });

      // Do request after cleaning
      axios({
        method: "post",
        url: "reg-by-link/check-status",
        data: {
          hash: this.hash,
          checkoutId: rPath,
        },
        config: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      })
        .then((response) => {
          this.paymentStatus = response.data;

          this.loading = false;
          this.error = false;
        })
        .catch((e) => {
          this.loading = false;
          this.error = true;
          console.error(e);

          this.handleRequestError(e);
        });
    },

    handleRequestError(e) {
      try {
        const res = e.response.data;
        if (res == undefined || res == null) {
          this.errorMessage = "An unknown error occured";
        }
        this.errorMessage = res.error ? res.error : "An unknown error occured";
      } catch (e) {
        this.errorMessage = "An unknown error occured";
      }
    },

    getLatestResourcePath() {
      // Get resourcePath from the current route's query parameters
      var rPath = this.$route.query.resourcePath;

      // If resourcePath is null, return null
      if (rPath == null) {
        return null;
      }

      // If resourcePath is an array, return the last element which will be the most recent checkout
      if (rPath.constructor === Array) {
        return rPath[rPath.length - 1];
      }

      // Otherwise, return the value of resourcePath
      return rPath;
    },

    retry() {
      this.loading = true;
      this.checkStatus = false;
      this.getRegByLink();
    },

    /**
    * Calculate the merchant logo image style based on its natural dimensions.
    * Banners will have a width of 400px.
    * Other images will fit within a 200x200 bounding box.
    */
    onImageLoad() {
      // Get the image element
      const imgElement = this.$refs.merchantLogo.image;
      if (!imgElement) {
        return;
      }

      // Get the natural dimensions from the image element
      const {width: naturalWidth, height: naturalHeight} = imgElement;
      if (!naturalWidth || !naturalHeight) {
        return;
      }

      // Calculate aspect ratio
      const aspectRatio = naturalWidth / naturalHeight;
      
      // If the image is more like a banner
      if (aspectRatio > 2) {
        this.merchantLogoImageStyle = {
          width: '400px',
          objectFit: 'contain',
        };
        return;
      }

      // Default max size for non-banner images
      const maxSize = 200;
      
      let scaledWidth, scaledHeight;

      // Scaling logic for non-banner images
      if (aspectRatio >= 1) {
        // Landscape or square
        scaledWidth = maxSize;
        scaledHeight = maxSize / aspectRatio;
      } else {
        // Portrait
        scaledWidth = maxSize * aspectRatio;
        scaledHeight = maxSize;
      }

      this.merchantLogoImageStyle = {
        width: `${scaledWidth}px`,
        height: `${scaledHeight}px`,
        objectFit: 'contain',
      };
    }

  },
};
</script>

<style scoped>
/* .text-styling {
  font-family: "Raleway", sans-serif;
} */

.title {
  font-size: 26px;
  font-weight: 400;
  line-height: 30px;
  padding-bottom: 15.5px;
}

.text-field-header {
  color: rgba(0, 0, 0, 0.5);
}
</style>
